import { type SocialCause } from '@koala/sdk';
import { useState } from 'react';
import { SocialCauseItem } from '@/components/account/social-cause-item/social-cause-item';
import { useSocialCauses } from '@/components/account/social-causes-modal/social-causes-modal-hooks';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { styled } from '@/components/content/stitches';
import { Stack } from '@/components/ui';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { Render } from '@/components/uielements/render';
import { useSelector } from '@/redux';
import { useGlobalDispatch } from '@/redux/global/reducer';
import * as ErrorReporter from '@/utils/errorReporter';
import { prepareErrorMessage } from '@/utils/global';

interface Props {
  toggle: () => void;
  onSubmit: (socialCause: Pick<SocialCause, 'id' | 'name'>) => Promise<void>;
  isLoading: boolean;
}

export function SocialCausesModal({ toggle, onSubmit, isLoading }: Props) {
  const { data, status } = useSocialCauses();
  const { label } = useSelector((state) => state.app.organization.organization);
  const [selectedSocialCauseId, setSelectedSocialCauseId] = useState<number>();
  const { dispatch, displayErrorToast } = useGlobalDispatch();

  if (status === 'error') {
    null;
  }

  return (
    <>
      <Modal.Title>
        <StringAccessor accessor="rewards.donate_reward_modal_header" dataObj={{ brand: label }} />
      </Modal.Title>

      <Modal.Content>
        <Stack direction="vertical" gap="$4">
          <Render condition={status === 'loading'}>
            <Paragraph>Fetching Social Causes...</Paragraph>
          </Render>

          <Render condition={Boolean(data?.data && data.data.length === 0)}>
            <Paragraph> Sorry, there are no Social Causes available </Paragraph>
          </Render>

          <Render condition={Boolean(data?.data && data.data.length > 0)}>
            <>
              <SubTitle>
                <StringAccessor accessor="rewards.donate_reward_modal_subheader" />
              </SubTitle>
              <Stack position="center" css={{ flexWrap: 'wrap' }}>
                {data?.data.map((cause) => (
                  <SocialCauseItem
                    isSelected={cause.id === selectedSocialCauseId}
                    key={cause.id}
                    {...cause}
                    onClick={setSelectedSocialCauseId}
                  />
                ))}
              </Stack>
            </>
          </Render>
        </Stack>
      </Modal.Content>

      <Modal.Footer>
        <Modal.Cancel>Cancel</Modal.Cancel>
        <StyledPrimaryButton
          data-css-override="StyledPrimaryButton"
          disabled={!selectedSocialCauseId || isLoading || status === 'loading'}
          onClick={async () => {
            if (!selectedSocialCauseId) {
              return;
            }

            const socialCause = data?.data.find((cause) => cause.id === selectedSocialCauseId);

            if (socialCause) {
              await onSubmit({ id: socialCause.id, name: socialCause.name });

              toggle();
            } else {
              const error = new Error('Social Cause not found');
              ErrorReporter.captureException(error);

              const errorMessage = await prepareErrorMessage(
                'Social Cause not found. Please try again.',
                error,
              );

              dispatch(displayErrorToast(errorMessage.message));
            }
          }}
        >
          <StringAccessor accessor="rewards.donate_reward_modal_donate_button" />
        </StyledPrimaryButton>
      </Modal.Footer>
    </>
  );
}

const SubTitle = styled('h2', {
  margin: 0,
});

const Paragraph = styled('p', {
  margin: 0,
  textAlign: 'center',
});
